import { setIntervalIfPossible } from "@faire/next-utils/isomorphic/setIntervalIfPossible";
import { differenceInDays } from "date-fns/differenceInDays";
import { differenceInHours } from "date-fns/differenceInHours";
import { differenceInMinutes } from "date-fns/differenceInMinutes";
import { differenceInSeconds } from "date-fns/differenceInSeconds";
import { useCallback, useEffect, useMemo, useState } from "react";

export const useFirstOrderIncentiveTimeRemaining = (expiresAt: number) => {
  const [timeNow, setTimeNow] = useState(Date.now());

  useEffect(() => {
    const interval = setIntervalIfPossible(() => setTimeNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const getRemainingTimeInterval = useCallback(() => {
    const timestamp = Math.min(timeNow, expiresAt);
    return {
      start: timestamp,
      end: expiresAt,
    };
  }, [expiresAt, timeNow]);

  const daysRemaining = useMemo(() => {
    const remainingTimeInterval = getRemainingTimeInterval();
    return differenceInDays(
      remainingTimeInterval.end,
      remainingTimeInterval.start
    );
  }, [getRemainingTimeInterval]);

  const hoursRemaining = useMemo(() => {
    const remainingTimeInterval = getRemainingTimeInterval();
    return differenceInHours(
      remainingTimeInterval.end,
      remainingTimeInterval.start
    );
  }, [getRemainingTimeInterval]);

  const timeRemaining = useMemo(() => {
    const toTwoDigits = (str: string) => str.padStart(2, "0");
    const remainingInterval = getRemainingTimeInterval();
    const totalHours = differenceInHours(
      remainingInterval.end,
      remainingInterval.start
    );
    const totalMinutes = differenceInMinutes(
      remainingInterval.end,
      remainingInterval.start
    );
    const totalSeconds = differenceInSeconds(
      remainingInterval.end,
      remainingInterval.start
    );
    const remainderMinutes = totalMinutes - totalHours * 60;
    const remainderSeconds = totalSeconds - totalMinutes * 60;

    return `${totalHours}:${toTwoDigits(`${remainderMinutes}`)}:${toTwoDigits(
      `${remainderSeconds}`
    )}`;
  }, [getRemainingTimeInterval]);

  return {
    daysRemaining,
    hoursRemaining,
    timeRemaining,
  };
};
